import { Input } from "@majitap/ui/arc/input/input";
import { useQuery } from "@tanstack/react-query";
import { Navigation } from "lucide-react";
import { type KeyboardEvent, useId, useRef, useState } from "react";
import { errorOf } from "../../api";
import { messageFor } from "../../error-copy";
import { MIN_QUERY, useDebounced } from "../../search";
import { addressPoint, findAddresses } from "../../zones";
import styles from "./address-field.module.css";

/** A Google Places result chosen in the field: its address and point. */
export interface PickedAddress {
  name: string;
  address: string;
  lat: number;
  lng: number;
}

/** Why there are no suggestions: no geocoding integration yet, or the provider failed. */
function noteFor(error: unknown): string {
  const { code } = errorOf(error);
  if (code === "INTEGRATION_NOT_CONFIGURED")
    return "Address search is off: set up Google Places in Settings › Integrations. Type the address, and pin it on the map.";
  return messageFor(errorOf(error));
}

/**
 * An address with suggestions from the company's Google Places integration (P8-41), wherever a
 * place or fill site is entered. Typing stays free text; picking a suggestion fills the address
 * and moves the pin (`onPick`). One session per search, so Google bills a search, not a keystroke.
 */
export function AddressField({
  label = "Address",
  value,
  onChange,
  onPick,
}: {
  label?: string;
  value: string;
  onChange: (address: string) => void;
  onPick: (picked: PickedAddress) => void;
}) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [note, setNote] = useState<string | null>(null);
  const session = useRef(crypto.randomUUID());
  const typed = value.trim();
  const q = useDebounced(typed);
  const ready = open && q.length >= MIN_QUERY;

  const suggestions = useQuery({
    queryKey: ["address-search", q],
    queryFn: () => findAddresses(q, session.current),
    enabled: ready,
    staleTime: 60_000,
    retry: false,
  });
  const options = ready ? (suggestions.data ?? []) : [];

  const pick = (placeId: string) => {
    setNote("Finding it on the map…");
    addressPoint(placeId, session.current).then(
      (p) => {
        // "Yaya Centre, Argwings Kodhek Rd, …": Google's address alone drops the building's name
        const address = !p.address ? p.name : p.address.startsWith(p.name) ? p.address : `${p.name}, ${p.address}`;
        onPick({ name: p.name, address, lat: p.latitude, lng: p.longitude });
        setOpen(false);
        setNote(null);
        session.current = crypto.randomUUID();
      },
      (error) => setNote(messageFor(errorOf(error))),
    );
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") return setOpen(false);
    if (!options.length) return;
    const step = { ArrowDown: 1, ArrowUp: -1 }[event.key];
    if (step) {
      event.preventDefault();
      setActive((active + step + options.length) % options.length);
    } else if (event.key === "Enter") {
      // a suggestion is picked, not the form submitted
      event.preventDefault();
      const option = options[active];
      if (option) pick(option.place_id);
    }
  };

  const showList = ready && (options.length > 0 || Boolean(suggestions.error) || Boolean(note));

  return (
    <div className={styles.field}>
      <Input
        label={label}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKeyDown}
        placeholder="Start typing: a building, street or area"
        autoComplete="off"
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={showList && options[active] ? `${listId}-${active}` : undefined}
      />
      {showList ? (
        <div className={styles.dropdown}>
          <div id={listId} role="listbox" aria-label="Addresses">
            {options.map((o, i) => (
              // biome-ignore lint/a11y/useKeyWithClickEvents: the field owns the keyboard (aria-activedescendant)
              <div
                key={o.place_id}
                id={`${listId}-${i}`}
                role="option"
                tabIndex={-1}
                aria-selected={i === active}
                className={styles.option}
                onMouseDown={(e) => e.preventDefault()}
                onMouseMove={() => setActive(i)}
                onClick={() => pick(o.place_id)}
              >
                <span className={styles.optionIcon}>
                  <Navigation size={15} aria-hidden />
                </span>
                <span className={styles.stack}>
                  <span>{o.title}</span>
                  {o.subtitle ? <span className={styles.muted}>{o.subtitle}</span> : null}
                </span>
              </div>
            ))}
          </div>
          {suggestions.error ? <p className={styles.note}>{noteFor(suggestions.error)}</p> : null}
          {note ? <p className={styles.note}>{note}</p> : null}
          <p className={styles.attribution}>Suggestions from Google</p>
        </div>
      ) : null}
    </div>
  );
}
